export type BevelHighlightMode = 'full' | 'top-edge';

interface BevelHighlightProps {
  /**
   * Button inner corner radius (in CSS pixels). Used by the native sibling
   * to trace its concentric highlight strokes along the chip's rounded
   * corners. The web variant ignores it — CSS `border-radius: inherit`
   * lets the browser's native inset shadow follow the wrapper's rounded
   * corners automatically.
   *
   * Kept in the props signature so both implementations expose the same
   * API and Button.tsx can pass `radius` unconditionally.
   */
  radius: number;
  /**
   * Rendering mode (accepted for API parity with the native sibling). The
   * CSS `box-shadow: inset 0 2px 2px 0` already paints only the top inside
   * edge, so both modes produce identical web output. Defaults to `'full'`.
   * See `BevelHighlight.native.tsx` for the mode semantics on RN.
   */
  mode?: BevelHighlightMode;
  /**
   * Inner-highlight colour — any CSS colour incl. `rgba(...)`. Defaults to
   * Figma's "Inner-Button" wash. Consumers that need a stronger/tinted sheen
   * (e.g. SrButton's glossy chips) override it.
   */
  glowColor?: string;
  /** Inset-shadow vertical offset (px). Default matches "Inner-Button" (2). */
  glowOffsetY?: number;
  /** Inset-shadow blur radius (px). Default matches "Inner-Button" (2). */
  glowBlur?: number;
  /**
   * Optional WEB-ONLY raw `box-shadow` list that REPLACES the computed
   * inset(s) — for bevels the structured props can't express (SrButton's dark
   * chip stacks a 1px near-white rim over the grey inner shadow). When
   * `webInsetShadowHover` is also set, the overlay cross-fades between the
   * two on `group-hover` (the Button Pressable is the group), so hover shows
   * EXACTLY the hover list, not the sum. The native sibling ignores both and
   * renders the structured glow props instead.
   */
  webInsetShadow?: string;
  webInsetShadowHover?: string;
  /**
   * Optional SECOND inset sheen on the BOTTOM inner edge — paints the lower
   * face of a two-sided bevel (top highlight + bottom highlight = a raised 3D
   * face). When `bottomColor` is set, a `inset 0 -{bottomOffsetY}px
   * {bottomBlur}px {bottomColor}` shadow is added. SrButton's glossy chips use
   * it; plain Button/SegmentedButton leave it unset (top-only).
   */
  bottomColor?: string;
  bottomOffsetY?: number;
  bottomBlur?: number;
}

/**
 * Inner bevel highlight — web variant.
 *
 * Renders Figma's "Inner-Button" effect exactly as specified:
 *
 *   box-shadow: 0 2px 2px 0 rgba(245, 243, 255, 0.30) inset;
 *
 * Implemented as a transparent absolutely-positioned `<div>` filling the
 * Button's inset wrapper. The wrapper supplies the rounded corners via
 * `rounded-btn-*` + `overflow-hidden`; this div sets `border-radius:
 * inherit` so the inset shadow follows the same rounded shape — including
 * asymmetric per-corner radii (e.g. a SegmentedButton segment with only
 * its outer corners rounded).
 *
 * No gradient, no SVG, no compositing tricks — just the browser-native CSS
 * inset shadow. NativeWind can't compile inset box-shadows on RN (it
 * crashes on the frozen RN style object), so the native sibling
 * (`BevelHighlight.native.tsx`) renders an SVG approximation instead — a
 * dense stack of concentric gradient-painted strokes that wrap the rounded
 * corners like the CSS shadow does. The native sibling exposes a `mode`
 * prop to swap between that perimeter bevel and SegmentedButton's
 * full-height wash; on web both modes look identical because the CSS inset
 * shadow already paints only the top edge.
 *
 * Defensive guard: this file should only render on web because Metro is
 * supposed to resolve to the `.native.tsx` sibling. The `typeof document`
 * check returns null on native if a stale Metro cache loads this by
 * mistake. Restart Metro with `--clear` after creating any new
 * `.native.tsx` sibling.
 */
export function BevelHighlight({
  radius: _radius,
  mode: _mode,
  glowColor = 'rgba(245, 243, 255, 0.30)',
  glowOffsetY = 2,
  glowBlur = 2,
  webInsetShadow,
  webInsetShadowHover,
  bottomColor,
  bottomOffsetY = 3,
  bottomBlur = 8,
}: BevelHighlightProps) {
  if (typeof document === 'undefined') return null;

  const shadows = [`inset 0 ${glowOffsetY}px ${glowBlur}px 0 ${glowColor}`];
  if (bottomColor) {
    // Negative Y → the sheen sits on the BOTTOM inner edge (the lower bevel
    // face). Listed after the top sheen so both composite on the one overlay.
    shadows.push(`inset 0 -${bottomOffsetY}px ${bottomBlur}px 0 ${bottomColor}`);
  }

  // Raw override wins over the computed list (see the prop doc).
  const restingShadow = webInsetShadow ?? shadows.join(', ');

  const overlayStyle = {
    position: 'absolute',
    inset: 0,
    borderRadius: 'inherit',
    pointerEvents: 'none',
  } as const;

  // No hover list → the single resting overlay (existing behaviour).
  if (webInsetShadowHover == null) {
    return <div aria-hidden style={{ ...overlayStyle, boxShadow: restingShadow }} />;
  }

  // Hover list → two overlays cross-faded on `group-hover`: the resting
  // bevel fades OUT while the hover bevel fades IN, so hover shows exactly
  // the hover list (not the sum). SrButton's dark-mode hover.
  return (
    <>
      <div
        aria-hidden
        className="transition-opacity duration-150 ease-out group-hover:opacity-0"
        style={{ ...overlayStyle, boxShadow: restingShadow }}
      />
      <div
        aria-hidden
        className="opacity-0 transition-opacity duration-150 ease-out group-hover:opacity-100"
        style={{ ...overlayStyle, boxShadow: webInsetShadowHover }}
      />
    </>
  );
}
